package pl.zar.tikthrow.ui.screen

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.BookmarkBorder
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.DragIndicator
import androidx.compose.material.icons.filled.FavoriteBorder
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.Tune
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material.icons.outlined.CloudOff
import androidx.compose.material.icons.outlined.Videocam
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.input.pointer.positionChange
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.zIndex
import androidx.lifecycle.viewmodel.compose.viewModel
import kotlin.math.roundToInt
import kotlin.time.Clock
import kotlinx.coroutines.delay
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.shared.domain.StalenessIndicator
import pl.zar.tikthrow.shared.dto.ChannelStats
import pl.zar.tikthrow.shared.dto.PlatformGroupDto
import pl.zar.tikthrow.shared.dto.PostDto
import pl.zar.tikthrow.ui.media.ImageFromUri
import pl.zar.tikthrow.ui.theme.*
import pl.zar.tikthrow.viewmodel.FeedUiState
import pl.zar.tikthrow.viewmodel.FeedViewModel
import pl.zar.tikthrow.viewmodel.PlanMode
import pl.zar.tikthrow.viewmodel.PublishedPost
import pl.zar.tikthrow.viewmodel.PublishedPostStatus
import pl.zar.tikthrow.viewmodel.PublishingPlan
import pl.zar.tikthrow.viewmodel.QueueViewModel
import pl.zar.tikthrow.viewmodel.QueuedPost
import pl.zar.tikthrow.viewmodel.WeeklySlot
import pl.zar.tikthrow.viewmodel.SavedPost
import pl.zar.tikthrow.viewmodel.SavedPostsViewModel
import pl.zar.tikthrow.viewmodel.ScheduledPost
import pl.zar.tikthrow.viewmodel.ScheduledPostsViewModel
import pl.zar.tikthrow.viewmodel.dayName
import pl.zar.tikthrow.viewmodel.isoDayNumber
import tikthrow.composeapp.generated.resources.Res
import tikthrow.composeapp.generated.resources.ic_discord
import tikthrow.composeapp.generated.resources.ic_facebook
import tikthrow.composeapp.generated.resources.ic_instagram
import tikthrow.composeapp.generated.resources.ic_tiktok
import tikthrow.composeapp.generated.resources.ic_twitter
import tikthrow.composeapp.generated.resources.ic_youtube

private val platformColors = mapOf(
    Platform.TWITTER   to Color(0xFF1D9BF0),
    Platform.INSTAGRAM to Color(0xFFE1306C),
    Platform.FACEBOOK  to Color(0xFF1877F2),
    Platform.TIKTOK    to Color(0xFFFF0050),
    Platform.YOUTUBE   to Color(0xFFFF0000),
    Platform.DISCORD   to Color(0xFF5865F2),
)

private val platformIcons: Map<Platform, DrawableResource> = mapOf(
    Platform.TWITTER   to Res.drawable.ic_twitter,
    Platform.INSTAGRAM to Res.drawable.ic_instagram,
    Platform.FACEBOOK  to Res.drawable.ic_facebook,
    Platform.TIKTOK    to Res.drawable.ic_tiktok,
    Platform.YOUTUBE   to Res.drawable.ic_youtube,
    Platform.DISCORD   to Res.drawable.ic_discord,
)

private val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))

private const val BLOCK_RECENTLY_PUBLISHED = "recently_published"
private const val BLOCK_ERRORS = "errors"
private const val BLOCK_SCHEDULED = "scheduled"
private const val BLOCK_ADD_CONTENT = "add_content"
private const val BLOCK_PLAN = "plan"
private const val BLOCK_STATS = "stats"
private const val BLOCK_TOP_POST = "top_post"
private const val BLOCK_ACTIVITY = "activity"
private const val BLOCK_CALENDAR = "calendar"
private const val BLOCK_DRAFTS = "drafts"
private fun platformBlockId(platform: Platform) = "platform:${platform.name}"

/** One user-configurable panel on the feed dashboard: a header (title/count/dot/trailing) plus its body. */
private class FeedBlock(
    val id: String,
    val title: String,
    val count: Int,
    val dotColor: Color? = null,
    /** True when the body renders placeholder data because no real data exists yet. */
    val isMock: Boolean = false,
    val trailing: (@Composable () -> Unit)? = null,
    val content: @Composable () -> Unit,
)

/** Merge the persisted block order with the blocks actually present today, appending newcomers at the end. */
private fun resolveBlockOrder(knownIds: List<String>, storedOrder: List<String>): List<String> {
    val known = knownIds.toSet()
    val fromStored = storedOrder.filter { it in known }
    val missing = knownIds.filter { it !in storedOrder }
    return fromStored + missing
}

/** Rough relative-height estimate used to balance blocks across dashboard columns. */
private fun estimateBlockCost(block: FeedBlock): Int = when (block.id) {
    BLOCK_RECENTLY_PUBLISHED -> 3 + ((block.count + 1) / 2) * 3
    BLOCK_ERRORS -> 5
    BLOCK_SCHEDULED -> 2 + minOf(block.count, 8) * 2
    BLOCK_ADD_CONTENT -> 2
    BLOCK_PLAN -> 9
    BLOCK_STATS -> 7
    BLOCK_TOP_POST -> 6
    BLOCK_ACTIVITY -> 2 + minOf(block.count, 6) * 2
    BLOCK_CALENDAR -> 5
    BLOCK_DRAFTS -> 2 + minOf(block.count, 6) * 2
    else -> 2 + minOf(block.count, 8) * 4
}

/** Greedy shortest-column-first placement so the dashboard stays roughly height-balanced. */
private fun distributeIntoColumns(order: List<String>, blocksById: Map<String, FeedBlock>, columns: Int): List<List<String>> {
    val heights = IntArray(columns)
    val perColumn = List(columns) { mutableListOf<String>() }
    order.forEach { id ->
        val cost = blocksById[id]?.let(::estimateBlockCost) ?: 2
        var minCol = 0
        for (c in 1 until columns) if (heights[c] < heights[minCol]) minCol = c
        heights[minCol] += cost
        perColumn[minCol].add(id)
    }
    return perColumn
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun FeedScreen(
    modifier: Modifier = Modifier,
    scheduledViewModel: ScheduledPostsViewModel,
    savedPostsViewModel: SavedPostsViewModel,
    queueViewModel: QueueViewModel,
    onNavigateToEditPlan: () -> Unit = {},
    onNavigateToAddQueuedPost: () -> Unit = {},
    onNavigateToAddPost: () -> Unit = {},
    onNavigateToAddVideo: () -> Unit = {},
    onNavigateToQueue: () -> Unit = {},
    viewModel: FeedViewModel = viewModel { FeedViewModel() }
) {
    val uiState by viewModel.uiState.collectAsState()
    val scheduledPosts by scheduledViewModel.posts.collectAsState()
    val publishedPosts by scheduledViewModel.publishedPosts.collectAsState()
    val savedPosts by savedPostsViewModel.posts.collectAsState()
    val queue by queueViewModel.queue.collectAsState()
    val plan by queueViewModel.plan.collectAsState()
    val layoutPrefs by viewModel.layout.collectAsState()
    var editMode by remember { mutableStateOf(false) }

    Column(modifier = modifier.fillMaxSize()) {
        TabScreenBackground {
            when (val state = uiState) {
                is FeedUiState.Loading -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = GradientStart)
                    }
                }
                is FeedUiState.Error -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Icon(
                                imageVector = Icons.Outlined.CloudOff,
                                contentDescription = "Brak połączenia",
                                tint = MaterialTheme.colorScheme.error,
                                modifier = Modifier.size(64.dp)
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                            Text(
                                text = "Błąd ładowania feeda",
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.titleMedium
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            Text(
                                text = state.message,
                                color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.6f),
                                style = MaterialTheme.typography.bodyMedium,
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                }
                is FeedUiState.Success -> {
                    val feed = state.feed
                    val grouped = feed.platformGroups
                    val nowMillis = Clock.System.now().toEpochMilliseconds()
                    val upcomingPosts = scheduledPosts
                        .filter {
                            scheduledViewModel.scheduledAtMillis(
                                it.scheduledDateMillis,
                                it.scheduledHour,
                                it.scheduledMinute
                            ) > nowMillis
                        }
                        .sortedBy {
                            scheduledViewModel.scheduledAtMillis(
                                it.scheduledDateMillis,
                                it.scheduledHour,
                                it.scheduledMinute
                            )
                        }
                    val recentPublished = remember(publishedPosts) {
                        publishedPosts
                            .filter { it.status == PublishedPostStatus.PUBLISHED }
                            .sortedByDescending { it.publishedAtMillis }
                            .take(6)
                    }
                    val recentFailed = remember(publishedPosts) {
                        publishedPosts
                            .filter { it.status == PublishedPostStatus.FAILED }
                            .sortedByDescending { it.publishedAtMillis }
                    }

                    val blocks = buildList {
                        add(FeedBlock(BLOCK_RECENTLY_PUBLISHED, "OSTATNIO OPUBLIKOWANE", recentPublished.size) {
                            if (recentPublished.isNotEmpty()) {
                                RecentlyPublishedSection(posts = recentPublished, nowMillis = nowMillis)
                            } else {
                                EmptyBlockHint("Tu pojawią się treści po pierwszej udanej publikacji.")
                            }
                        })
                        add(FeedBlock(BLOCK_ERRORS, "BŁĘDY PUBLIKACJI", recentFailed.size, dotColor = Rose500) {
                            if (recentFailed.isNotEmpty()) {
                                PublishErrorsPanel(failedPosts = recentFailed)
                            } else {
                                EmptyBlockHint("Brak nieudanych publikacji — wszystko poszło gładko.")
                            }
                        })
                        add(FeedBlock(BLOCK_SCHEDULED, "ZAPLANOWANE", upcomingPosts.size) {
                            if (upcomingPosts.isNotEmpty()) {
                                ScheduledBlockContent(posts = upcomingPosts, scheduledViewModel = scheduledViewModel, nowMillis = nowMillis)
                            } else {
                                EmptyBlockHint("Nic nie czeka w kolejce. Zaplanuj treść, żeby zobaczyć ją tutaj.")
                            }
                        })

                        add(FeedBlock(BLOCK_ADD_CONTENT, "DODAJ TREŚĆ", 0) {
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                AddActionButton(
                                    label = "Dodaj wideo",
                                    modifier = Modifier.weight(1f),
                                    onClick = onNavigateToAddVideo,
                                )
                                AddActionButton(
                                    label = "Dodaj post",
                                    modifier = Modifier.weight(1f),
                                    onClick = onNavigateToAddPost,
                                )
                            }
                        })

                        val planSlots = queueViewModel.effectiveSlots(plan)
                        val pendingQueue = queue.filter { it.status != "FAILED" }
                        val failedQueueCount = queue.count { it.status == "FAILED" }
                        // One estimated slot per queued item, so each entry can show when it goes out.
                        val queueTimes = remember(plan, queue, nowMillis / 60_000L) {
                            queueViewModel.nextSlots(pendingQueue.size.coerceAtLeast(1))
                        }
                        val nextTestAttemptMillis = remember(plan, nowMillis / 60_000L) {
                            queueViewModel.testModeNextAttemptMillis()
                        }
                        add(FeedBlock(BLOCK_PLAN, "PLAN PUBLIKACJI", pendingQueue.size) {
                            PublishingPlanContent(
                                plan = plan,
                                slots = planSlots,
                                queue = pendingQueue,
                                queueTimes = queueTimes,
                                failedCount = failedQueueCount,
                                nextTestAttemptMillis = nextTestAttemptMillis,
                                weeksOfContent = queueViewModel.queueDurationWeeks(),
                                nowMillis = nowMillis,
                                onToggleActive = queueViewModel::togglePlanActive,
                                onOpenEditor = onNavigateToEditPlan,
                                onAddContent = onNavigateToAddQueuedPost,
                                onMoveUp = queueViewModel::moveUp,
                                onMoveDown = queueViewModel::moveDown,
                                onRemove = queueViewModel::removePost,
                                onUpdateContent = queueViewModel::updateContent,
                                onSetTestInterval = queueViewModel::setTestInterval,
                                onViewErrors = onNavigateToQueue,
                                onViewQueue = onNavigateToQueue,
                            )
                        })

                        // Real per-channel counts from `GET /api/v1/stats` (one row per
                        // channel with published/scheduled/failed totals).
                        val stats = remember(state.channelStats) { weeklyStatsFromChannels(state.channelStats) }
                        add(FeedBlock(BLOCK_STATS, "STATYSTYKI TYGODNIA", stats.size) {
                            if (stats.isEmpty()) EmptyBlockHint("Brak publikacji — statystyki pojawią się po pierwszej publikacji.")
                            else WeeklyStatsContent(stats)
                        })
                        add(FeedBlock(BLOCK_TOP_POST, "NAJLEPSZY POST", 0) {
                            EmptyBlockHint("Brak danych o popularności postów — ta metryka nie jest jeszcze liczona przez serwer.")
                        })
                        add(FeedBlock(BLOCK_ACTIVITY, "AKTYWNOŚĆ", 0) {
                            EmptyBlockHint("Brak danych o aktywności — tę metrykę serwiser jeszcze nie zapisuje.")
                        })
                        // Real next-7-days schedule derived from the queue's estimated slot
                        // timestamps (one slot per pending post, ordered by publish time).
                        val calendarDays = remember(queueTimes, nowMillis / 3_600_000L) {
                            calendarDaysFromSlots(queueTimes, nowMillis)
                        }
                        add(FeedBlock(BLOCK_CALENDAR, "NAJBLIŻSZE 7 DNI", queueTimes.size) {
                            CalendarContent(calendarDays)
                        })
                        val draftsAreMock = savedPosts.isEmpty()
                        val drafts = if (draftsAreMock) remember { mockDrafts() } else savedPosts
                        add(FeedBlock(BLOCK_DRAFTS, "SZKICE", drafts.size, isMock = draftsAreMock) {
                            DraftsContent(drafts)
                        })
                        grouped.forEach { platformGroup ->
                            if (platformGroup.posts.isNotEmpty()) {
                                val color = platformColors[platformGroup.platform] ?: GradientStart
                                add(FeedBlock(
                                    id = platformBlockId(platformGroup.platform),
                                    title = platformGroup.platform.displayName.uppercase(),
                                    count = platformGroup.posts.size,
                                    dotColor = color,
                                    trailing = if (feed.stalenessIndicator != StalenessIndicator.FRESH) {
                                        {
                                            Icon(
                                                imageVector = Icons.Outlined.CloudOff,
                                                contentDescription = "Dane nieaktualne",
                                                tint = Slate400,
                                                modifier = Modifier.size(14.dp)
                                            )
                                        }
                                    } else null
                                ) {
                                    PlatformBlockContent(
                                        posts = platformGroup.posts,
                                        onSave = { postId -> viewModel.toggleBookmark(postId) },
                                        onLike = { postId -> viewModel.toggleLike(postId) }
                                    )
                                })
                            }
                        }
                    }

                    Column(modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState())) {
                        Spacer(modifier = Modifier.height(18.dp))
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = "tikthrow",
                                style = TextStyle(
                                    brush = gradientBrush,
                                    fontSize = 30.sp,
                                    fontWeight = FontWeight.ExtraBold,
                                ),
                                modifier = Modifier.weight(1f)
                            )
                            Surface(
                                onClick = { editMode = !editMode },
                                shape = RoundedCornerShape(8.dp),
                                color = if (editMode) GradientStart.copy(alpha = 0.14f) else Color.Transparent,
                            ) {
                                Row(
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                                ) {
                                    Icon(
                                        imageVector = if (editMode) Icons.Filled.Check else Icons.Filled.Tune,
                                        contentDescription = "Dostosuj widok",
                                        tint = if (editMode) GradientStart else Slate500,
                                        modifier = Modifier.size(18.dp)
                                    )
                                    Text(
                                        text = if (editMode) "Gotowe" else "Dostosuj",
                                        fontSize = 13.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = if (editMode) GradientStart else Slate500
                                    )
                                }
                            }
                        }
                        Spacer(modifier = Modifier.height(18.dp))

                        val knownIds = blocks.map { it.id }
                        val resolvedOrder = remember(knownIds, layoutPrefs.order) {
                            resolveBlockOrder(knownIds, layoutPrefs.order)
                        }
                        val blocksById = remember(blocks) { blocks.associateBy { it.id } }

                        FeedDashboard(
                            resolvedOrder = resolvedOrder,
                            blocksById = blocksById,
                            hidden = layoutPrefs.hidden,
                            editMode = editMode,
                            onToggleVisibility = viewModel::toggleBlockVisibility,
                            onReorder = viewModel::setBlockOrder
                        )
                        Spacer(modifier = Modifier.height(24.dp))
                    }
                }
            }
        }
    }
}

@Composable
private fun FeedDashboard(
    resolvedOrder: List<String>,
    blocksById: Map<String, FeedBlock>,
    hidden: Set<String>,
    editMode: Boolean,
    onToggleVisibility: (String) -> Unit,
    onReorder: (List<String>) -> Unit,
) {
    val visibleOrder = if (editMode) resolvedOrder else resolvedOrder.filter { it !in hidden }
    if (visibleOrder.isEmpty()) return

    BoxWithConstraints(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
        val columns = when {
            maxWidth < 700.dp -> 1
            maxWidth < 1100.dp -> 2
            else -> 3
        }
        // Resting positions of each block, recorded before any drag translation is applied.
        val blockBounds = remember { mutableStateMapOf<String, Rect>() }
        // Grab-handle hit areas, in window coordinates.
        val handleBounds = remember { mutableStateMapOf<String, Rect>() }
        var draggedId by remember { mutableStateOf<String?>(null) }
        // Absolute pointer position in window coordinates; survives layout reflow mid-drag.
        var pointerWindow by remember { mutableStateOf(Offset.Zero) }
        // Grab point inside the dragged card, so it doesn't snap its corner to the cursor.
        var grabInCard by remember { mutableStateOf(Offset.Zero) }
        var previewOrder by remember { mutableStateOf<List<String>?>(null) }
        var containerTopLeft by remember { mutableStateOf(Offset.Zero) }
        // Last slot we swapped into. Moving a block reflows the grid, which shifts every other
        // block's bounds; without this latch the pointer can land back on the block it just
        // displaced and the two would swap forever.
        var lastTargetId by remember { mutableStateOf<String?>(null) }

        // While dragging, the grid lays out from the preview order so the user sees the result live.
        val effectiveOrder = previewOrder?.filter { it in visibleOrder } ?: visibleOrder
        val columnContents = distributeIntoColumns(effectiveOrder, blocksById, columns)

        fun retarget() {
            val current = draggedId ?: return
            val order = (previewOrder ?: visibleOrder).toMutableList()
            val candidates = blockBounds.entries.filter { it.key != current && it.key in order }
            if (candidates.isEmpty()) return
            // Only commit a move once the pointer is genuinely over another block's slot; hovering
            // empty space leaves the preview as-is rather than guessing.
            val hit = candidates.firstOrNull { it.value.contains(pointerWindow) } ?: return
            if (hit.key == lastTargetId) return
            val from = order.indexOf(current)
            val to = order.indexOf(hit.key)
            if (from < 0 || to < 0 || from == to) return
            order.removeAt(from)
            order.add(to, current)
            previewOrder = order
            lastTargetId = hit.key
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .onGloballyPositioned { containerTopLeft = it.boundsInWindow().topLeft }
                // The gesture lives on this container, not on the cards. A card is disposed the
                // moment it turns into a placeholder or reflows into another column, which would
                // cancel a gesture owned by the card — the drag would restart and cancel on every
                // frame instead of moving.
                .pointerInput(editMode) {
                    if (!editMode) return@pointerInput
                    awaitEachGesture {
                        val down = awaitFirstDown(requireUnconsumed = false)
                        val downWindow = containerTopLeft + down.position
                        val hitId = handleBounds.entries
                            .firstOrNull { it.value.contains(downWindow) }?.key
                        // Not on a grab handle — leave the event alone so scrolling and the
                        // hide/show buttons keep working.
                            ?: return@awaitEachGesture
                        val startBounds = blockBounds[hitId] ?: return@awaitEachGesture

                        down.consume()
                        draggedId = hitId
                        grabInCard = downWindow - startBounds.topLeft
                        pointerWindow = downWindow
                        previewOrder = visibleOrder
                        lastTargetId = null

                        var committed = false
                        while (true) {
                            val event = awaitPointerEvent()
                            val change = event.changes.firstOrNull { it.id == down.id }
                            if (change == null) break
                            if (!change.pressed) {
                                committed = true
                                break
                            }
                            val delta = change.positionChange()
                            if (delta != Offset.Zero) {
                                pointerWindow += delta
                                change.consume()
                                retarget()
                            }
                        }

                        val finalOrder = previewOrder
                        if (committed && finalOrder != null) onReorder(finalOrder)
                        draggedId = null
                        previewOrder = null
                        lastTargetId = null
                    }
                }
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                repeat(columns) { colIndex ->
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        columnContents[colIndex].forEach { id ->
                            val block = blocksById[id] ?: return@forEach
                            key(id) {
                                if (draggedId == id) {
                                    // The card itself floats in the overlay; its slot shows where it will land.
                                    DropPlaceholder(height = blockBounds[id]?.height ?: 0f)
                                } else {
                                    FeedBlockCard(
                                        block = block,
                                        isHidden = id in hidden,
                                        editMode = editMode,
                                        isDragging = false,
                                        onBoundsChanged = { rect -> blockBounds[id] = rect },
                                        onHandleBoundsChanged = { rect -> handleBounds[id] = rect },
                                        onToggleVisibility = { onToggleVisibility(id) },
                                    )
                                }
                            }
                        }
                    }
                }
            }

            // Overlay: drawn after the columns, so the dragged card floats above every column.
            val dragging = draggedId
            if (dragging != null) {
                val block = blocksById[dragging]
                val rect = blockBounds[dragging]
                if (block != null && rect != null) {
                    val topLeft = pointerWindow - grabInCard - containerTopLeft
                    Box(
                        modifier = Modifier
                            .zIndex(1f)
                            .offset { IntOffset(topLeft.x.roundToInt(), topLeft.y.roundToInt()) }
                            .width(with(LocalDensity.current) { rect.width.toDp() })
                    ) {
                        FeedBlockCard(
                            block = block,
                            isHidden = dragging in hidden,
                            editMode = editMode,
                            isDragging = true,
                            onBoundsChanged = {},
                            onHandleBoundsChanged = {},
                            onToggleVisibility = {},
                        )
                    }
                }
            }
        }
    }
}

/** Outlined gap showing the slot the dragged block will drop into. */
@Composable
private fun DropPlaceholder(height: Float) {
    val heightDp = with(LocalDensity.current) { height.toDp() }
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(if (heightDp > 0.dp) heightDp else 72.dp)
            .clip(RoundedCornerShape(10.dp))
            .background(GradientStart.copy(alpha = 0.08f))
            .border(1.5.dp, GradientStart.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
    )
}

@Composable
private fun FeedBlockCard(
    block: FeedBlock,
    isHidden: Boolean,
    editMode: Boolean,
    isDragging: Boolean,
    onBoundsChanged: (Rect) -> Unit,
    /** Reports the grab handle's hit area so the container can own the drag gesture. */
    onHandleBoundsChanged: (Rect) -> Unit,
    onToggleVisibility: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .onGloballyPositioned { coords -> onBoundsChanged(coords.boundsInWindow()) }
    ) {
        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(10.dp),
            color = if (isHidden) Slate100.copy(alpha = 0.4f) else Slate100,
            border = when {
                isDragging -> BorderStroke(1.5.dp, GradientStart)
                editMode -> BorderStroke(1.dp, Slate200)
                else -> null
            },
            shadowElevation = if (isDragging) 12.dp else 0.dp,
        ) {
            Column {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (editMode) {
                        // Generous hit area — the bare 18dp icon is an awkward grab target.
                        Box(
                            modifier = Modifier
                                .size(32.dp)
                                .onGloballyPositioned { onHandleBoundsChanged(it.boundsInWindow()) },
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                imageVector = Icons.Filled.DragIndicator,
                                contentDescription = "Przeciagnij, aby zmienic kolejnosc",
                                tint = if (isDragging) GradientStart else Slate500,
                                modifier = Modifier.size(18.dp)
                            )
                        }
                        Spacer(Modifier.width(4.dp))
                    }
                    block.dotColor?.let { color ->
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(color)
                        )
                        Spacer(Modifier.width(8.dp))
                    }
                    Text(
                        text = if (block.count > 0) "${block.title} · ${block.count}" else block.title,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.ExtraBold,
                        letterSpacing = 1.2.sp,
                        color = block.dotColor ?: Slate500,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f)
                    )
                    if (block.isMock) {
                        MockBadge()
                        Spacer(Modifier.width(6.dp))
                    }
                    block.trailing?.invoke()
                    if (editMode) {
                        Spacer(Modifier.width(6.dp))
                        IconButton(onClick = onToggleVisibility, modifier = Modifier.size(24.dp)) {
                            Icon(
                                imageVector = if (isHidden) Icons.Filled.Visibility else Icons.Filled.Close,
                                contentDescription = if (isHidden) "Pokaz blok" else "Ukryj blok",
                                tint = if (isHidden) GradientStart else Slate500,
                                modifier = Modifier.size(15.dp)
                            )
                        }
                    }
                }
                if (!isHidden) {
                    block.content()
                    Spacer(Modifier.height(10.dp))
                } else if (editMode) {
                    Text(
                        text = "Ukryty — dotknij oka, aby przywrocic",
                        fontSize = 11.sp,
                        color = Slate400,
                        modifier = Modifier.padding(horizontal = 14.dp, vertical = 10.dp)
                    )
                }
            }
        }
    }
}

/** Marks a block whose body is placeholder content rather than real data. */
@Composable
private fun MockBadge() {
    Surface(shape = RoundedCornerShape(4.dp), color = Slate300) {
        Text(
            text = "przykład",
            modifier = Modifier.padding(horizontal = 5.dp, vertical = 1.dp),
            fontSize = 9.sp,
            fontWeight = FontWeight.SemiBold,
            color = Slate600
        )
    }
}

@Composable
private fun EmptyBlockHint(text: String) {
    Text(
        text = text,
        fontSize = 12.sp,
        color = Slate400,
        modifier = Modifier.padding(horizontal = 14.dp, vertical = 6.dp)
    )
}

/**
 * Feed-side summary of the publishing plan: whether the automatic queue is running, how far the
 * backlog stretches, which weekly slots it fires on, and what goes out next. Mirrors the plan the
 * Kolejka tab configures; editing still happens on the full plan screen.
 */
@Composable
private fun PublishingPlanContent(
    plan: PublishingPlan,
    slots: List<WeeklySlot>,
    queue: List<QueuedPost>,
    queueTimes: List<Long>,
    failedCount: Int,
    nextTestAttemptMillis: Long?,
    weeksOfContent: Int,
    nowMillis: Long,
    onToggleActive: () -> Unit,
    onOpenEditor: () -> Unit,
    onAddContent: () -> Unit,
    onMoveUp: (String) -> Unit,
    onMoveDown: (String) -> Unit,
    onRemove: (String) -> Unit,
    onUpdateContent: (id: String, title: String, caption: String) -> Unit,
    onSetTestInterval: (Int?) -> Unit,
    onViewErrors: () -> Unit,
    onViewQueue: () -> Unit = {},
) {
    val queueSize = queue.size
    var editing by remember { mutableStateOf<QueuedPost?>(null) }
    // Expanding the tile shows the whole backlog inline so it can be reordered without leaving
    // the feed; collapsing it back to the first three keeps the dashboard tile compact.
    var expanded by remember { mutableStateOf(false) }

    editing?.let { post ->
        QueuedPostEditDialog(
            post = post,
            onDismiss = { editing = null },
            onSave = { title, caption ->
                onUpdateContent(post.id, title, caption)
                editing = null
            }
        )
    }
    Column(
        modifier = Modifier.padding(horizontal = 12.dp),
        verticalArrangement = Arrangement.spacedBy(7.dp)
    ) {
        // Status, mode and the on/off switch on one line — the numbers below carry the detail,
        // so this row stays a single glanceable statement instead of a boxed-in panel.
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(7.dp)
                    .clip(CircleShape)
                    .background(if (plan.isActive) Emerald500 else Slate400)
            )
            Spacer(Modifier.width(7.dp))
            Text(
                text = if (plan.isActive) "Aktywny" else "Wstrzymany",
                fontSize = 12.sp,
                fontWeight = FontWeight.Bold,
                color = if (plan.isActive) Slate900 else Slate500
            )
            Text(
                text = if (plan.mode == PlanMode.SLOTS) " · sloty tygodniowe"
                       else " · ${plan.postsPerWeek}× w tygodniu",
                fontSize = 10.sp,
                color = Slate500,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f)
            )
            Switch(
                checked = plan.isActive,
                onCheckedChange = { onToggleActive() },
                colors = SwitchDefaults.colors(
                    checkedThumbColor = Color.White,
                    checkedTrackColor = GradientStart,
                    uncheckedThumbColor = Slate500,
                    uncheckedTrackColor = Slate300,
                ),
                modifier = Modifier.scale(0.75f)
            )
        }

        // Three stat tiles became one line: same numbers, a fraction of the height.
        Text(
            text = buildString {
                append("$queueSize w kolejce")
                if (slots.isNotEmpty()) append(" · zapas ~$weeksOfContent tyg. · ${slots.size} slotów/tydz.")
            },
            fontSize = 10.sp,
            fontWeight = FontWeight.SemiBold,
            color = Slate500
        )

        if (slots.isEmpty()) {
            Text(
                text = "Brak slotów — ustaw, kiedy treści mają wychodzić.",
                fontSize = 10.sp,
                color = Slate400
            )
        } else {
            FlowSlotChips(slots)
        }

        if (queue.isEmpty()) {
            Text(
                text = "Kolejka pusta — dodaj treści, a plan wypuści je w swoich slotach.",
                fontSize = 10.sp,
                color = Slate400
            )
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                val visible = if (expanded) queue else queue.take(3)
                visible.forEachIndexed { index, post ->
                    QueuedPlanItem(
                        position = index + 1,
                        post = post,
                        timeMillis = queueTimes.getOrNull(index),
                        nowMillis = nowMillis,
                        isFirst = index == 0,
                        isNext = index == 0 && plan.isActive && plan.testIntervalMinutes != null,
                        isLast = index == queue.lastIndex,
                        onMoveUp = { onMoveUp(post.id) },
                        onMoveDown = { onMoveDown(post.id) },
                        onRemove = { onRemove(post.id) },
                        onEdit = { editing = post },
                    )
                }
                if (queue.size > 3) {
                    Text(
                        text = if (expanded) "Zwiń kolejkę" else "+${queue.size - 3} dalej w kolejce — rozwiń",
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(6.dp))
                            .clickable { expanded = !expanded }
                            .padding(vertical = 5.dp),
                        fontSize = 10.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = GradientStart,
                        textAlign = TextAlign.Center
                    )
                }
                Text(
                    text = "Zobacz całą kolejkę →",
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(6.dp))
                        .clickable { onViewQueue() }
                        .padding(vertical = 4.dp),
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    color = Slate500,
                    textAlign = TextAlign.Center
                )
            }
        }

        // Both warnings share one line — they're status, not content, so they shouldn't each
        // claim a full-width banner. A missing description still publishes; a failure doesn't,
        // so only the failure half is tappable (it leads to the Kolejka tab where it's fixable).
        val missingDescriptions = queue.count { it.caption.isBlank() }
        if (missingDescriptions > 0 || failedCount > 0) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                if (failedCount > 0) {
                    Row(
                        modifier = Modifier.clickable { onViewErrors() },
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        Icon(Icons.Filled.Warning, contentDescription = null, tint = Rose500, modifier = Modifier.size(11.dp))
                        Text(
                            text = "$failedCount nieudane →",
                            fontSize = 10.sp,
                            fontWeight = FontWeight.Bold,
                            color = Rose500
                        )
                    }
                }
                if (missingDescriptions > 0) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        Icon(Icons.Filled.Warning, contentDescription = null, tint = Amber400, modifier = Modifier.size(11.dp))
                        Text(
                            text = "$missingDescriptions bez opisu",
                            fontSize = 10.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = Amber400
                        )
                    }
                }
            }
        }

        TestModePanel(
            intervalMinutes = plan.testIntervalMinutes,
            queueSize = queueSize,
            planIsActive = plan.isActive,
            nextAttemptMillis = nextTestAttemptMillis,
            onSetInterval = onSetTestInterval,
        )

        AddActionButton(
            label = "Dodaj treść do kolejki",
            modifier = Modifier.fillMaxWidth(),
            onClick = onAddContent,
        )

        Text(
            text = "Edytuj harmonogram",
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(6.dp))
                .clickable { onOpenEditor() }
                .padding(vertical = 6.dp),
            fontSize = 11.sp,
            fontWeight = FontWeight.Bold,
            color = GradientStart,
            textAlign = TextAlign.Center
        )
    }
}

/**
 * A gradient "add" action used both as its own draggable dashboard tile (BLOCK_ADD_CONTENT,
 * halved into video/post) and embedded full-width inside the plan block. [enabled] = false
 * renders it muted with an optional [hint] ("wkrótce") instead of wiring up a fake destination.
 */
@Composable
private fun AddActionButton(
    label: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    hint: String? = null,
    onClick: () -> Unit,
) {
    Surface(
        onClick = onClick,
        enabled = enabled,
        shape = RoundedCornerShape(6.dp),
        color = Color.Transparent,
        modifier = modifier
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(if (enabled) gradientBrush else Brush.linearGradient(listOf(Slate300, Slate300)))
                .padding(vertical = 10.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(1.dp)
        ) {
            Row(horizontalArrangement = Arrangement.Center, verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    Icons.Filled.Add,
                    contentDescription = null,
                    modifier = Modifier.size(16.dp),
                    tint = if (enabled) Color.White else Slate500
                )
                Spacer(Modifier.width(6.dp))
                Text(
                    text = label,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = if (enabled) Color.White else Slate500
                )
            }
            if (hint != null) {
                Text(
                    text = hint,
                    fontSize = 9.sp,
                    fontWeight = FontWeight.Medium,
                    color = if (enabled) Color.White.copy(alpha = 0.85f) else Slate500
                )
            }
        }
    }
}

private val TEST_INTERVAL_CHOICES = listOf(2, 5, 15)

/**
 * Test mode: publish one queued post every N minutes instead of waiting for the weekly slots.
 *
 * This runs the real publishing path — same tokens, same upload — so the warning is deliberate.
 * TikTok stays SELF_ONLY (the app is unaudited), but YouTube uploads are real.
 */
@Composable
private fun TestModePanel(
    intervalMinutes: Int?,
    queueSize: Int,
    planIsActive: Boolean,
    nextAttemptMillis: Long?,
    onSetInterval: (Int?) -> Unit,
) {
    val active = intervalMinutes != null
    Surface(
        shape = RoundedCornerShape(8.dp),
        color = if (active) Amber400.copy(alpha = 0.12f) else Slate200,
        border = if (active) BorderStroke(1.dp, Amber400.copy(alpha = 0.5f)) else null,
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 7.dp),
            verticalArrangement = Arrangement.spacedBy(5.dp)
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(1.dp)) {
                    Text(
                        text = "Tryb testowy",
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        color = if (active) Amber400 else Slate900
                    )
                    Text(
                        text = when {
                            active -> "Publikuje co $intervalMinutes min zamiast wg harmonogramu"
                            !planIsActive -> "Publikuje co kilka minut, żeby dało się przetestować. Włączenie aktywuje też plan."
                            else -> "Publikuje co kilka minut, żeby dało się przetestować"
                        },
                        fontSize = 9.sp,
                        color = Slate500
                    )
                }
                Switch(
                    checked = active,
                    onCheckedChange = { on -> onSetInterval(if (on) TEST_INTERVAL_CHOICES.first() else null) },
                    colors = SwitchDefaults.colors(
                        checkedThumbColor = Color.White,
                        checkedTrackColor = Amber400,
                        uncheckedThumbColor = Slate500,
                        uncheckedTrackColor = Slate300,
                    ),
                    modifier = Modifier.scale(0.8f)
                )
            }
            if (active) {
                Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                    TEST_INTERVAL_CHOICES.forEach { minutes ->
                        val selected = minutes == intervalMinutes
                        Surface(
                            onClick = { onSetInterval(minutes) },
                            shape = RoundedCornerShape(4.dp),
                            color = if (selected) Amber400.copy(alpha = 0.25f) else Slate200,
                        ) {
                            Text(
                                text = "$minutes min",
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 3.dp),
                                fontSize = 9.sp,
                                fontWeight = if (selected) FontWeight.Bold else FontWeight.Medium,
                                color = if (selected) Amber400 else Slate500
                            )
                        }
                    }
                }
                Text(
                    text = if (queueSize == 0) {
                        "Kolejka pusta — nic nie zostanie wysłane."
                    } else {
                        "Uwaga: publikuje naprawdę. $queueSize " +
                            (if (queueSize == 1) "treść zejdzie" else "treści zejdzie") +
                            " w ok. ${queueSize * (intervalMinutes ?: 0)} min. Wyłącz po teście."
                    },
                    fontSize = 9.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Amber400
                )
                if (queueSize > 0) {
                    TestModeCountdown(nextAttemptMillis)
                }
            }
        }
    }
}

/** Live MM:SS countdown to the next test-mode publish attempt, ticking independently of the rest of the screen. */
@Composable
private fun TestModeCountdown(nextAttemptMillis: Long?) {
    var remainingSeconds by remember(nextAttemptMillis) {
        mutableStateOf(nextAttemptMillis?.let { secondsUntil(it) })
    }
    LaunchedEffect(nextAttemptMillis) {
        while (nextAttemptMillis != null) {
            remainingSeconds = secondsUntil(nextAttemptMillis)
            delay(1000)
        }
    }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(5.dp)
    ) {
        Text(
            text = "Następna publikacja za",
            fontSize = 9.sp,
            fontWeight = FontWeight.SemiBold,
            color = Amber400
        )
        val secs = remainingSeconds
        Text(
            text = if (secs == null) "..." else if (secs <= 0L) "lada moment" else secs.toCountdownLabel(),
            fontSize = 12.sp,
            fontWeight = FontWeight.ExtraBold,
            fontFamily = FontFamily.Monospace,
            color = Amber400
        )
    }
}

private fun secondsUntil(targetMillis: Long): Long =
    ((targetMillis - Clock.System.now().toEpochMilliseconds()) / 1000L).coerceAtLeast(0L)

private fun Long.toCountdownLabel(): String {
    val m = this / 60
    val s = this % 60
    return "${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}"
}

/** One backlog entry: what goes out, when, and controls to reorder or drop it. */
@Composable
private fun QueuedPlanItem(
    position: Int,
    post: QueuedPost,
    timeMillis: Long?,
    nowMillis: Long,
    isFirst: Boolean,
    isNext: Boolean,
    isLast: Boolean,
    onMoveUp: () -> Unit,
    onMoveDown: () -> Unit,
    onRemove: () -> Unit,
    onEdit: () -> Unit,
) {
    Surface(
        onClick = onEdit,
        shape = RoundedCornerShape(6.dp),
        color = if (isNext) Amber400.copy(alpha = 0.12f) else Slate200,
        border = if (isNext) BorderStroke(1.dp, Amber400) else null,
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 6.dp, vertical = 5.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            FeedThumbnail(post.media, size = 26.dp)
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(1.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text("$position.", fontSize = 9.sp, fontWeight = FontWeight.ExtraBold, color = GradientStart)
                    Text(
                        text = post.title.ifBlank { post.caption }.ifBlank { post.type.label },
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        color = Slate900,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f, fill = false)
                    )
                    if (isNext) {
                        Surface(shape = RoundedCornerShape(3.dp), color = Amber400) {
                            Text(
                                text = "NASTĘPNY",
                                modifier = Modifier.padding(horizontal = 4.dp, vertical = 1.dp),
                                fontSize = 7.sp,
                                fontWeight = FontWeight.ExtraBold,
                                color = Color.White
                            )
                        }
                    }
                }
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(5.dp)) {
                    FeedPlatformDotsRow(post.platforms)
                    Text(
                        text = timeMillis?.let { futureTimeLabel(it, nowMillis) } ?: "poza planem",
                        fontSize = 9.sp,
                        fontWeight = FontWeight.Medium,
                        color = Slate500,
                        maxLines = 1
                    )
                    if (post.caption.isBlank()) {
                        Text("· bez opisu", fontSize = 9.sp, fontWeight = FontWeight.Bold, color = Amber400, maxLines = 1)
                    }
                }
                QueuedPostTargetWarning(post)
            }
            // Laid out horizontally so the row's height is set by the thumbnail, not by a
            // stacked pair of arrows.
            Row(verticalAlignment = Alignment.CenterVertically) {
                IconButton(onClick = onMoveUp, enabled = !isFirst, modifier = Modifier.size(16.dp)) {
                    Icon(
                        Icons.Filled.KeyboardArrowUp,
                        contentDescription = "W górę",
                        tint = if (isFirst) Slate300 else Slate500,
                        modifier = Modifier.size(13.dp)
                    )
                }
                IconButton(onClick = onMoveDown, enabled = !isLast, modifier = Modifier.size(16.dp)) {
                    Icon(
                        Icons.Filled.KeyboardArrowDown,
                        contentDescription = "W dół",
                        tint = if (isLast) Slate300 else Slate500,
                        modifier = Modifier.size(13.dp)
                    )
                }
                IconButton(onClick = onRemove, modifier = Modifier.size(16.dp)) {
                    Icon(Icons.Filled.Close, contentDescription = "Usuń z kolejki", tint = Slate500, modifier = Modifier.size(12.dp))
                }
            }
        }
    }
}

/** Weekly slots as a single horizontally-scrolling row of chips, so the tile's height never depends on how many slots the plan has. */
@Composable
private fun FlowSlotChips(slots: List<WeeklySlot>) {
    val sorted = remember(slots) { slots.sortedWith(compareBy({ it.dayOfWeek }, { it.hour }, { it.minute })) }
    LazyRow(horizontalArrangement = Arrangement.spacedBy(3.dp)) {
        items(sorted) { slot ->
            Surface(shape = RoundedCornerShape(3.dp), color = GradientStart.copy(alpha = 0.12f)) {
                Text(
                    text = "${slot.dayOfWeek.dayName} ${slot.hour.toString().padStart(2, '0')}:${slot.minute.toString().padStart(2, '0')}",
                    modifier = Modifier.padding(horizontal = 4.dp, vertical = 2.dp),
                    fontSize = 9.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = GradientStart,
                    maxLines = 1
                )
            }
        }
    }
}

@Composable
private fun WeeklyStatsContent(stats: List<WeeklyStat>) {
    Column(
        modifier = Modifier.padding(horizontal = 12.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        stats.chunked(2).forEach { row ->
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                row.forEach { stat ->
                    Surface(
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(8.dp),
                        color = Slate200,
                    ) {
                        Column(modifier = Modifier.padding(10.dp), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                            Text(stat.label, fontSize = 10.sp, fontWeight = FontWeight.SemiBold, color = Slate500)
                            Text(stat.value, fontSize = 19.sp, fontWeight = FontWeight.ExtraBold, color = Slate900)
                            val up = stat.deltaPercent >= 0
                            Text(
                                text = "${if (up) "+" else ""}${stat.deltaPercent}% t/t",
                                fontSize = 10.sp,
                                fontWeight = FontWeight.Bold,
                                color = if (up) Emerald500 else Rose500
                            )
                        }
                    }
                }
                repeat(2 - row.size) { Spacer(Modifier.weight(1f)) }
            }
        }
    }
}

@Composable
private fun CalendarContent(days: List<CalendarDay>) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        days.forEach { day ->
            val active = day.postCount > 0
            Surface(
                modifier = Modifier.weight(1f),
                shape = RoundedCornerShape(6.dp),
                color = if (active) GradientStart.copy(alpha = 0.14f) else Slate200,
            ) {
                Column(
                    modifier = Modifier.padding(vertical = 8.dp, horizontal = 2.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(3.dp)
                ) {
                    Text(
                        text = day.dayLabel,
                        fontSize = 9.sp,
                        fontWeight = FontWeight.Bold,
                        color = if (active) GradientStart else Slate500,
                        maxLines = 1
                    )
                    Text(day.dateLabel, fontSize = 10.sp, color = Slate500, maxLines = 1)
                    Text(
                        text = if (active) "${day.postCount}" else "–",
                        fontSize = 13.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = if (active) GradientStart else Slate400
                    )
                }
            }
        }
    }
}

@Composable
private fun DraftsContent(drafts: List<SavedPost>) {
    if (drafts.isEmpty()) {
        EmptyBlockHint("Brak szkiców. Zapisz treść bez terminu, żeby wróciła tutaj.")
        return
    }
    Column(
        modifier = Modifier.padding(horizontal = 12.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        drafts.take(6).forEach { draft ->
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(8.dp),
                color = Slate200,
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    FeedThumbnail(draft.media)
                    Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(
                            text = draft.caption.ifBlank { "Szkic bez opisu" },
                            fontSize = 13.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = Slate900,
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis
                        )
                        FeedPlatformDotsRow(draft.platforms)
                    }
                }
            }
        }
        if (drafts.size > 6) {
            Text(
                text = "+${drafts.size - 6} kolejnych",
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                color = Slate500
            )
        }
    }
}

@Composable
private fun FeedThumbnail(
    media: List<pl.zar.tikthrow.ui.media.MediaItem>,
    modifier: Modifier = Modifier,
    size: androidx.compose.ui.unit.Dp = 48.dp,
) {
    val first = media.firstOrNull()
    Box(
        modifier = modifier
            .size(size)
            .clip(RoundedCornerShape(8.dp))
            .background(Slate300),
        contentAlignment = Alignment.Center
    ) {
        if (first != null) {
            ImageFromUri(uri = first.uri, isVideo = first.isVideo, modifier = Modifier.matchParentSize())
        } else {
            // Posts loaded back from the server carry no local file — only the queue metadata
            // round-trips over the API — so there's no real frame to show. A plain empty box
            // reads as a broken image; this at least says "video" instead of looking wrong.
            Icon(
                imageVector = Icons.Outlined.Videocam,
                contentDescription = null,
                tint = Slate500,
                modifier = Modifier.size(size * 0.5f)
            )
        }
    }
}

@Composable
private fun FeedPlatformDotsRow(platforms: Set<Platform>) {
    if (platforms.isEmpty()) return
    Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
        platforms.take(5).forEach { platform ->
            val icon = platformIcons[platform]
            if (icon != null) {
                Icon(
                    painter = painterResource(icon),
                    contentDescription = platform.displayName,
                    modifier = Modifier.size(13.dp),
                    tint = platformColors[platform] ?: Slate400
                )
            }
        }
    }
}

private fun futureTimeLabel(millis: Long, nowMillis: Long): String {
    val tz = TimeZone.currentSystemDefault()
    val target = Instant.fromEpochMilliseconds(millis).toLocalDateTime(tz)
    val time = "${target.hour.toString().padStart(2, '0')}:${target.minute.toString().padStart(2, '0')}"
    val targetMidnight = LocalDateTime(target.date, LocalTime(0, 0)).toInstant(tz).toEpochMilliseconds()
    val nowDate = Instant.fromEpochMilliseconds(nowMillis).toLocalDateTime(tz).date
    val nowMidnight = LocalDateTime(nowDate, LocalTime(0, 0)).toInstant(tz).toEpochMilliseconds()
    val dayDiff = ((targetMidnight - nowMidnight) / 86_400_000L).toInt()
    return when {
        dayDiff == 0 -> "dziś $time"
        dayDiff == 1 -> "jutro $time"
        dayDiff in 2..6 -> "${target.dayOfWeek.isoDayNumber.dayName} $time"
        else -> "${formatDateMillis(millis)} $time"
    }
}

@Composable
private fun RecentlyPublishedSection(posts: List<PublishedPost>, nowMillis: Long) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp)) {
        val columns = (maxWidth / 108.dp).toInt().coerceAtLeast(2)
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            posts.chunked(columns).forEach { rowPosts ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    rowPosts.forEach { post ->
                        RecentlyPublishedCard(
                            post = post,
                            nowMillis = nowMillis,
                            modifier = Modifier.weight(1f)
                        )
                    }
                    repeat(columns - rowPosts.size) {
                        Spacer(modifier = Modifier.weight(1f))
                    }
                }
            }
        }
    }
}

@Composable
private fun RecentlyPublishedCard(post: PublishedPost, nowMillis: Long, modifier: Modifier = Modifier) {
    val media = post.media.firstOrNull()
    Surface(
        modifier = modifier.aspectRatio(0.8f),
        shape = RoundedCornerShape(8.dp),
        color = Slate200,
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            if (media != null) {
                ImageFromUri(uri = media.uri, isVideo = media.isVideo, modifier = Modifier.matchParentSize())
            } else {
                Box(modifier = Modifier.matchParentSize(), contentAlignment = Alignment.Center) {
                    Text(
                        text = post.caption.take(40).ifBlank { post.type.label },
                        fontSize = 11.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Slate500,
                        textAlign = TextAlign.Center,
                        maxLines = 4,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.padding(8.dp)
                    )
                }
            }
            if (media?.isVideo == true) {
                Icon(
                    imageVector = Icons.Outlined.Videocam,
                    contentDescription = null,
                    tint = Color.White,
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(6.dp)
                        .size(14.dp)
                )
            }
            Box(
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .fillMaxWidth()
                    .background(
                        Brush.verticalGradient(
                            listOf(Color.Transparent, Color.Black.copy(alpha = 0.80f))
                        )
                    )
                    .padding(horizontal = 8.dp, vertical = 8.dp)
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    if (post.platforms.isNotEmpty()) {
                        Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                            post.platforms.take(4).forEach { platform ->
                                val icon = platformIcons[platform]
                                if (icon != null) {
                                    Icon(
                                        painter = painterResource(icon),
                                        contentDescription = platform.displayName,
                                        modifier = Modifier.size(12.dp),
                                        tint = platformColors[platform] ?: Slate400
                                    )
                                }
                            }
                        }
                    }
                    Text(
                        text = relativeTimeLabel(post.publishedAtMillis, nowMillis),
                        fontSize = 10.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Color.White.copy(alpha = 0.92f)
                    )
                }
            }
        }
    }
}

private fun relativeTimeLabel(millis: Long, nowMillis: Long): String {
    val diff = (nowMillis - millis).coerceAtLeast(0L)
    val minutes = diff / 60_000L
    val hours = diff / 3_600_000L
    val days = diff / 86_400_000L
    return when {
        minutes < 1 -> "przed chwilą"
        minutes < 60 -> "$minutes min temu"
        hours < 24 -> "$hours godz. temu"
        days < 7 -> "$days dni temu"
        else -> formatDateMillis(millis)
    }
}

@Composable
private fun PublishErrorsPanel(failedPosts: List<PublishedPost>) {
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp),
        shape = RoundedCornerShape(6.dp),
        color = Rose500.copy(alpha = 0.08f),
    ) {
        Row(modifier = Modifier.height(IntrinsicSize.Min)) {
            Box(
                modifier = Modifier
                    .width(3.dp)
                    .fillMaxHeight()
                    .background(Rose500)
            )
            Column(
                modifier = Modifier
                    .padding(horizontal = 10.dp, vertical = 10.dp)
                    .weight(1f),
                verticalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    Icon(
                        imageVector = Icons.Filled.Warning,
                        contentDescription = null,
                        tint = Rose500,
                        modifier = Modifier.size(13.dp)
                    )
                    Text(
                        text = if (failedPosts.size == 1) "Nie udało się opublikować 1 treści"
                               else "Nie udało się opublikować ${failedPosts.size} treści",
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold,
                        color = Rose500
                    )
                }
                failedPosts.take(2).forEach { post ->
                    Column(verticalArrangement = Arrangement.spacedBy(1.dp)) {
                        Text(
                            text = post.caption.ifBlank { post.type.label },
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = Slate900,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        Text(
                            text = post.message.ifBlank { "Nieznany błąd publikacji" },
                            fontSize = 11.sp,
                            color = Slate500,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
                if (failedPosts.size > 2) {
                    Text(
                        text = "+${failedPosts.size - 2} więcej w Kolejce",
                        fontSize = 10.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Rose500
                    )
                }
            }
        }
    }
}

@Composable
private fun ScheduledBlockContent(posts: List<ScheduledPost>, scheduledViewModel: ScheduledPostsViewModel, nowMillis: Long) {
    Column(
        modifier = Modifier.padding(horizontal = 12.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        posts.take(8).forEach { post ->
            FeedScheduledPostCard(
                post = post,
                timeMillis = scheduledViewModel.scheduledAtMillis(
                    post.scheduledDateMillis, post.scheduledHour, post.scheduledMinute
                ),
                nowMillis = nowMillis,
            )
        }
        if (posts.size > 8) {
            Text(
                text = "+${posts.size - 8} kolejnych",
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                color = Slate500
            )
        }
    }
}

@Composable
private fun FeedScheduledPostCard(post: ScheduledPost, timeMillis: Long, nowMillis: Long, modifier: Modifier = Modifier) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
        color = Slate200,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            FeedThumbnail(post.media)
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = post.title.ifBlank { post.caption }.ifBlank { post.type.label },
                    fontSize = 15.sp,
                    fontWeight = FontWeight.Bold,
                    color = Slate900,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    FeedPlatformDotsRow(post.platforms)
                    Text(
                        text = futureTimeLabel(timeMillis, nowMillis),
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium,
                        color = Slate500
                    )
                }
            }
        }
    }
}

@Composable
private fun PlatformBlockContent(
    posts: List<PostDto>,
    onSave: (String) -> Unit,
    onLike: (String) -> Unit,
) {
    Column(modifier = Modifier.padding(horizontal = 12.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        posts.take(8).forEach { post ->
            PostCard(
                post = post,
                onSave = { onSave(post.id) },
                onLike = { onLike(post.id) }
            )
        }
        if (posts.size > 8) {
            Text(
                text = "+${posts.size - 8} kolejnych postów",
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                color = Slate500
            )
        }
    }
}

@Composable
private fun PostCard(
    post: PostDto,
    onSave: () -> Unit,
    onLike: () -> Unit
) {
    val platformColor = platformColors[post.platform] ?: GradientStart

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(6.dp),
        color = Slate200,
        shadowElevation = 0.dp
    ) {
        Row(modifier = Modifier.height(IntrinsicSize.Min)) {
            Column(
                modifier = Modifier
                    .padding(14.dp)
                    .weight(1f)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(38.dp)
                            .clip(CircleShape)
                            .background(platformColor.copy(alpha = 0.14f)),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = post.authorName.firstOrNull()?.uppercase() ?: "?",
                            color = platformColor,
                            fontWeight = FontWeight.ExtraBold,
                            fontSize = 15.sp
                        )
                    }
                    Spacer(Modifier.width(12.dp))
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = post.authorName,
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Bold,
                            color = Slate900,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        Text(
                            text = post.platform.displayName,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = platformColor
                        )
                    }
                }

                post.textContent?.takeIf { it.isNotBlank() }?.let { text ->
                    Spacer(Modifier.height(10.dp))
                    Text(
                        text = text,
                        fontSize = 14.sp,
                        lineHeight = 20.sp,
                        color = Slate800,
                        maxLines = 4,
                        overflow = TextOverflow.Ellipsis
                    )
                }

                Spacer(Modifier.height(10.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = "${post.likeCount} polubień",
                        fontSize = 12.sp,
                        color = Slate500,
                        modifier = Modifier.weight(1f)
                    )
                    IconButton(onClick = onLike, modifier = Modifier.size(36.dp)) {
                        Icon(
                            imageVector = Icons.Filled.FavoriteBorder,
                            contentDescription = "Polub",
                            modifier = Modifier.size(18.dp),
                            tint = Slate500
                        )
                    }
                    IconButton(onClick = onSave, modifier = Modifier.size(36.dp)) {
                        Icon(
                            imageVector = Icons.Filled.BookmarkBorder,
                            contentDescription = "Zapisz",
                            modifier = Modifier.size(18.dp),
                            tint = Slate500
                        )
                    }
                }
            }
        }
    }
}


// ---------------------------------------------------------------------------
// Mock data for the feed blocks that have no backend yet. They render sample
// data so the dashboard can be laid out and previewed end to end; replace them
// with real sources when those blocks get an API (T8-T15).
// ---------------------------------------------------------------------------

private data class WeeklyStat(val label: String, val value: String, val deltaPercent: Int)

/** Real per-channel counts from `GET /api/v1/stats`, one `WeeklyStat` row per metric per channel. */
private fun weeklyStatsFromChannels(channels: List<ChannelStats>): List<WeeklyStat> =
    channels.flatMap { ch ->
        listOf(
            WeeklyStat("Opublikowane · ${ch.platform}", ch.published.toString(), 0),
            WeeklyStat("Zaplanowane · ${ch.platform}", ch.scheduled.toString(), 0),
            WeeklyStat("Błędy · ${ch.platform}", ch.failed.toString(), 0),
        )
    }

/**
 * Real "next 7 days" schedule: bins the queue's estimated slot timestamps (epoch millis,
 * one per pending post, ordered by publish time) into the current + next 6 calendar days.
 */
private fun calendarDaysFromSlots(slotMillis: List<Long>, nowMillis: Long): List<CalendarDay> {
    val tz = TimeZone.currentSystemDefault()
    val start = Instant.fromEpochMilliseconds(nowMillis).toLocalDateTime(tz).date
    val counts = IntArray(7) { 0 }
    slotMillis.forEach { slot ->
        val date = Instant.fromEpochMilliseconds(slot).toLocalDateTime(tz).date
        val offset = date.minus(start).days
        if (offset in 0..6) counts[offset] += 1
    }
    return (0..6).map { offset ->
        val date = start.plus(offset, DateTimeUnit.DAY)
        CalendarDay(
            dayLabel = if (offset == 0) "Dziś" else date.dayOfWeek.isoDayNumber.dayName,
            dateLabel = "${date.dayOfMonth.toString().padStart(2, '0')}." +
                "${date.monthNumber.toString().padStart(2, '0')}",
            postCount = counts[offset],
        )
    }
}

private data class CalendarDay(val dayLabel: String, val dateLabel: String, val postCount: Int)

private fun mockDrafts(): List<SavedPost> = listOf(
    SavedPost(
        id = "mock-draft-1",
        media = emptyList(),
        caption = "Trening poranny — wersja bez ścieżki dźwiękowej",
        platforms = setOf(Platform.TIKTOK, Platform.YOUTUBE),
    ),
    SavedPost(
        id = "mock-draft-2",
        media = emptyList(),
        caption = "Zwiastun trasy kajaka: Olsztyn -> Węgorzewo",
        platforms = setOf(Platform.TIKTOK),
    ),
)

/** Inline editor for the title/caption a queued post will publish with (opened from the plan tile). */
@Composable
private fun QueuedPostEditDialog(
    post: QueuedPost,
    onDismiss: () -> Unit,
    onSave: (title: String, caption: String) -> Unit,
) {
    var title by remember(post.id) { mutableStateOf(post.title) }
    var caption by remember(post.id) { mutableStateOf(post.caption) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Slate100,
        title = {
            Text(
                text = "Edytuj post z kolejki",
                fontSize = 15.sp,
                fontWeight = FontWeight.ExtraBold,
                color = Slate900
            )
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value = title,
                    onValueChange = { if (it.length <= 100) title = it },
                    label = { Text("Tytuł") },
                    placeholder = { Text("Tytuł (np. dla YouTube)", color = Slate400) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Slate600, unfocusedBorderColor = Slate200,
                        focusedTextColor = Slate900, unfocusedTextColor = Slate900,
                        cursorColor = Slate800,
                        focusedContainerColor = Slate50, unfocusedContainerColor = Slate50,
                    ),
                    shape = RoundedCornerShape(6.dp)
                )
                OutlinedTextField(
                    value = caption,
                    onValueChange = { caption = it },
                    label = { Text("Opis") },
                    placeholder = { Text("Opis publikacji (YouTube / TikTok)", color = Slate400) },
                    modifier = Modifier.fillMaxWidth().heightIn(min = 100.dp),
                    maxLines = 6,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Slate600, unfocusedBorderColor = Slate200,
                        focusedTextColor = Slate900, unfocusedTextColor = Slate900,
                        cursorColor = Slate800,
                        focusedContainerColor = Slate50, unfocusedContainerColor = Slate50,
                    ),
                    shape = RoundedCornerShape(6.dp)
                )
            }
        },
        confirmButton = {
            TextButton(onClick = { onSave(title, caption) }) {
                Text("Zapisz", fontSize = 14.sp, fontWeight = FontWeight.Bold, color = Slate900)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Anuluj", fontSize = 14.sp, fontWeight = FontWeight.Medium, color = Slate500)
            }
        }
    )
}

/** Small hint when a queued post targets a platform with no linked account. */
@Composable
private fun QueuedPostTargetWarning(post: QueuedPost) {
    val missing = buildList {
        if (Platform.YOUTUBE in post.platforms && post.youtubeAccountId == null) add("YouTube")
        if (Platform.TIKTOK in post.platforms && post.tiktokAccountId == null) add("TikTok")
    }
    if (missing.isEmpty()) return
    Text(
        text = "· bez konta: ${missing.joinToString(", ")}",
        fontSize = 9.sp,
        fontWeight = FontWeight.Bold,
        color = Amber400,
        maxLines = 1
    )
}
